import styles from './ListGalleryModule.module.scss';

import cx from 'classnames';
import React, { FC, HTMLAttributes } from 'react';
import ReactMarkdown from 'react-markdown';

import { Subject } from 'api/contentful/contentTypes.cloverdotcom';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { Image } from 'components/Image/Image';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { SubjectImage } from 'components/SubjectImage/SubjectImage';
import { TextV2 } from 'components/TextV2/TextV2';
import checkIcon from 'images/icons/check-green.svg';
import rightArrowIcon from 'images/icons/right-arrow-green.svg';
import { FlatEntry } from 'services/cms/flattenContentful';
import { toEntry, toSysId } from 'services/cms/typesUtil';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';

type ListGalleryItemProps = {
  item?: FlatEntry<Subject>;
} & HTMLAttributes<HTMLDivElement>;

export const ListGalleryItem: FC<ListGalleryItemProps> = ({ item, className, ...props }) => {
  if (!item) return null;
  const { blurb, richBlurb, image, link, secondaryLinks = [], title } = item;
  const hasSecondaryLinks = secondaryLinks.some(l => l);
  return (
    <div className={cx(styles.item, className)} {...props}>
      {image && (
        <FadeToastUp>
          <div className={styles.imageWrapper}>
            <SubjectImage className={styles.image} entry={item} />
          </div>
        </FadeToastUp>
      )}
      {(title || blurb || richBlurb || link || hasSecondaryLinks) && (
        <FadeToastUp>
          <div className={cx(styles.textWrapper, title && styles.gapLg)}>
            {title && <TextV2 className={styles.heading} tagName="h3" value={title} variant="modern-h3" />}
            {link && (
              <ContentLinkV2 className={styles.link} entry={toEntry(link)} hideTitle>
                <TextV2 tagName="h3" value={link.title} variant="modern-h4" />
                <Image src={rightArrowIcon} aria-hidden="true" alt="" />
              </ContentLinkV2>
            )}
            {richBlurb && <RichBlurb document={richBlurb} />}
            {blurb && !richBlurb && (
              <ReactMarkdown
                className={styles.blurb}
                source={blurb}
                renderers={{
                  listItem: ({ children }) => (
                    <li className={styles.listItem}>
                      <Image src={checkIcon} aria-hidden="true" alt="" />
                      {children}
                    </li>
                  ),
                }}
              />
            )}
            {hasSecondaryLinks && (
              <div className={styles.secondaryLinks}>
                {secondaryLinks.map(secondaryLink => {
                  if (!secondaryLink) return null;
                  return (
                    <ContentLinkV2
                      key={toSysId(secondaryLink)}
                      className={styles.link}
                      entry={toEntry(secondaryLink)}
                      hideTitle
                    >
                      <TextV2 value={secondaryLink.title} variant="modern-body" />
                      <Image src={rightArrowIcon} aria-hidden="true" alt="" />
                    </ContentLinkV2>
                  );
                })}
              </div>
            )}
          </div>
        </FadeToastUp>
      )}
    </div>
  );
};
